import {
  CmpEventAttendanceModeEnum,
  CmpEventCustomFieldInput,
  CmpEventCustomFieldReturn,
  CmpEventCustomFieldScopeEnum,
  CmpEventCustomFieldTypeEnum,
  CmpEventDataInput,
  CmpEventDressCodeEnum,
  CmpEventFieldsFragment,
  CmpEventLocationModeEnum,
  CmpEventStatusEnum,
  CmpEventTicketAudienceEnum,
  CmpEventVisibilityEnum,
  GetCmpEventTypesQuery,
} from "../../../graphql/types";
import {
  EventCustomField,
  EventFormState,
  EventRegistrationQuestion,
  EventStatus,
  EventTypeOption,
  EventVisibility,
  createBlankEventFormState,
  createDefaultRegistrationQuestions,
} from "./eventMockData";
import {
  dateTimeInputPartsToUtcMilli,
  normalizeCmpTimeZone,
  utcMilliToDateTimeInputParts,
} from "../../../utils/cmpTimeZones";
import { centsToCurrencyValue } from "../../common/dashboard/currencyUnits";

type EventTypeReturn =
  GetCmpEventTypesQuery["getCmpEventTypes"]["eventTypes"][number];

const parseCents = (value: string): number => {
  const normalizedValue = value.replace(/[^0-9.-]/g, "");
  const parsedValue = Number(normalizedValue || "0");

  return Number.isFinite(parsedValue) ? Math.round(parsedValue * 100) : 0;
};

const formatCents = (value?: number | null) =>
  `$${((value || 0) / 100).toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const parseOptionalNumber = (value: string): number | null => {
  const parsedValue = Number(value);

  return value.trim() && Number.isFinite(parsedValue) ? parsedValue : null;
};

const dateInputToUtcMilli = ({
  boundary,
  timeZone,
  value,
}: {
  boundary: "start" | "end";
  timeZone: string;
  value?: string;
}): number | undefined => {
  if (!value) {
    return undefined;
  }

  return dateTimeInputPartsToUtcMilli(
    value,
    boundary === "start" ? "00:00" : "23:59",
    timeZone,
  );
};

// Must invert dateInputToUtcMilli exactly: sales dates are stored as 00:00 /
// 23:59 in the CHAMBER's zone, so reading them back through UTC (toISOString)
// shifted the end boundary +1 day per load/save cycle in every UTC-negative
// chamber zone.
const utcMilliToDateInput = (
  value: number | null | undefined,
  timeZone: string,
): string => (value ? utcMilliToDateTimeInputParts(value, timeZone).date : "");

const getDressCodeEnum = (
  dressCode?: EventFormState["dressCode"],
): CmpEventDressCodeEnum | undefined => {
  switch (dressCode) {
    case "Formal":
      return CmpEventDressCodeEnum.Formal;
    case "Business Attire":
      return CmpEventDressCodeEnum.BusinessAttire;
    case "Casual":
      return CmpEventDressCodeEnum.Casual;
    default:
      return undefined;
  }
};

const getDressCodeLabel = (
  dressCode?: CmpEventDressCodeEnum | null,
): EventFormState["dressCode"] => {
  switch (dressCode) {
    case CmpEventDressCodeEnum.Formal:
      return "Formal";
    case CmpEventDressCodeEnum.BusinessAttire:
      return "Business Attire";
    case CmpEventDressCodeEnum.Casual:
      return "Casual";
    default:
      return "";
  }
};

export const toEventTypeOptions = (
  eventTypes: EventTypeReturn[],
): EventTypeOption[] =>
  eventTypes.map((eventType) => ({
    id: eventType.id,
    label: eventType.label,
    color: eventType.color,
    isArchived: eventType.isArchived,
  }));

export const buildBlankEventFormState = (
  eventTypes: EventTypeOption[],
): EventFormState => createBlankEventFormState(eventTypes[0]);

const getStatusLabel = (status: CmpEventStatusEnum): EventStatus => {
  switch (status) {
    case CmpEventStatusEnum.Published:
      return "Published";
    case CmpEventStatusEnum.Live:
      return "Live";
    case CmpEventStatusEnum.Completed:
      return "Completed";
    case CmpEventStatusEnum.Canceled:
      return "Cancelled";
    case CmpEventStatusEnum.Draft:
    default:
      return "Draft";
  }
};

const getVisibilityLabel = (
  visibility: CmpEventVisibilityEnum,
): EventVisibility =>
  visibility === CmpEventVisibilityEnum.Private ? "Private" : "Public";

const getFieldTypeLabel = (
  fieldType: CmpEventCustomFieldTypeEnum,
): EventCustomField["type"] => {
  switch (fieldType) {
    case CmpEventCustomFieldTypeEnum.Select:
      return "Select";
    case CmpEventCustomFieldTypeEnum.MultiSelect:
      return "Multi-select";
    case CmpEventCustomFieldTypeEnum.Checkbox:
      return "Checkbox";
    case CmpEventCustomFieldTypeEnum.FileUpload:
      return "File upload";
    case CmpEventCustomFieldTypeEnum.Text:
    case CmpEventCustomFieldTypeEnum.Textarea:
    case CmpEventCustomFieldTypeEnum.Date:
    default:
      return "Text";
  }
};

const formatCustomFieldChoices = (choices: string[]): string =>
  choices.join(", ");

const parseCustomFieldChoices = (choices: string): string[] =>
  choices
    .split(",")
    .map((choice) => choice.trim())
    .filter(Boolean);

const getCustomFieldChoicesForSave = ({
  backendChoices,
  choices,
}: Pick<EventCustomField, "backendChoices" | "choices">): string[] =>
  backendChoices && formatCustomFieldChoices(backendChoices) === choices
    ? [...backendChoices]
    : parseCustomFieldChoices(choices);

const defaultRegistrationQuestions = createDefaultRegistrationQuestions();
const registrationQuestionIds = new Set(
  defaultRegistrationQuestions.map((question) => question.id),
);
const registrationQuestionPresetKeys = new Set(
  defaultRegistrationQuestions.map((question) => question.presetKey),
);
const REGISTRATION_QUESTION_SETTINGS_VERSION = 1;
const normalizeRegistrationQuestionLabel = (label: string): string =>
  label.trim().toLocaleLowerCase("en-CA");
const registrationQuestionIdByLabel = new Map([
  ...defaultRegistrationQuestions.map((question): [string, string] => [
    normalizeRegistrationQuestionLabel(question.adminTitle),
    question.id,
  ]),
  // Events saved by the legacy custom-field UI used a generated id and this
  // public-facing label for the speaker preset.
  [
    normalizeRegistrationQuestionLabel(
      "Submit a question to the speaker or panelists.",
    ),
    "question-speaker",
  ] as [string, string],
]);

type RegistrationQuestionCustomField = Pick<
  CmpEventCustomFieldReturn,
  | "appliesToSponsorshipPackageIds"
  | "appliesToTicketTypeIds"
  | "choices"
  | "fieldType"
  | "helperText"
  | "id"
  | "label"
  | "required"
  | "scope"
  | "sortOrder"
>;

type CustomFieldsToRegistrationQuestionsInput = {
  customFields: RegistrationQuestionCustomField[];
  registrationQuestionSettingsJson?: string | null;
};

type RegistrationQuestionSetting = {
  id: string;
  adminTitle: string;
  description: string;
  enabled: boolean;
  choices: Array<{ id: string; enabled: boolean }>;
  label: string;
  helperText: string;
  customFieldId: string;
  required: boolean;
  backendFieldType: CmpEventCustomFieldTypeEnum;
  scope: CmpEventCustomFieldScopeEnum;
  appliesToTicketTypeIds: string[];
  appliesToSponsorshipPackageIds: string[];
  sortOrder: number;
};

type RegistrationQuestionSettings = {
  version: typeof REGISTRATION_QUESTION_SETTINGS_VERSION;
  questions: RegistrationQuestionSetting[];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((entry) => typeof entry === "string");

const customFieldTypeValues = new Set<string>(
  Object.values(CmpEventCustomFieldTypeEnum),
);
const customFieldScopeValues = new Set<string>(
  Object.values(CmpEventCustomFieldScopeEnum),
);

const isCustomFieldType = (
  value: unknown,
): value is CmpEventCustomFieldTypeEnum =>
  typeof value === "string" && customFieldTypeValues.has(value);

const isCustomFieldScope = (
  value: unknown,
): value is CmpEventCustomFieldScopeEnum =>
  typeof value === "string" && customFieldScopeValues.has(value);

const getDefaultRegistrationQuestionFieldType = (
  question: EventRegistrationQuestion,
): CmpEventCustomFieldTypeEnum =>
  question.kind === "multiSelect"
    ? CmpEventCustomFieldTypeEnum.MultiSelect
    : CmpEventCustomFieldTypeEnum.Textarea;

const withRegistrationQuestionMetadata = (
  question: EventRegistrationQuestion,
  index: number,
): EventRegistrationQuestion => ({
  ...question,
  customFieldId: question.customFieldId || question.id,
  required: question.required ?? false,
  backendFieldType:
    question.backendFieldType ||
    getDefaultRegistrationQuestionFieldType(question),
  scope: question.scope || CmpEventCustomFieldScopeEnum.Attendee,
  appliesToTicketTypeIds: question.appliesToTicketTypeIds || [],
  appliesToSponsorshipPackageIds: question.appliesToSponsorshipPackageIds || [],
  sortOrder: question.sortOrder ?? index,
});

const expectedFieldTypesByQuestionId = new Map<string, Set<string>>(
  defaultRegistrationQuestions.map((question) => [
    question.id,
    new Set(
      question.kind === "multiSelect"
        ? [CmpEventCustomFieldTypeEnum.MultiSelect]
        : [
            CmpEventCustomFieldTypeEnum.Text,
            CmpEventCustomFieldTypeEnum.Textarea,
          ],
    ),
  ]),
);

const getRegistrationQuestionIdForCustomField = (
  customField: RegistrationQuestionCustomField,
): string | undefined => {
  if (registrationQuestionIds.has(customField.id)) {
    return customField.id;
  }

  const questionId = registrationQuestionIdByLabel.get(
    normalizeRegistrationQuestionLabel(customField.label),
  );
  if (
    !questionId ||
    customField.scope !== CmpEventCustomFieldScopeEnum.Attendee ||
    !expectedFieldTypesByQuestionId.get(questionId)?.has(customField.fieldType)
  ) {
    return undefined;
  }

  return questionId;
};

export const isRegistrationQuestionCustomField = (
  customField: RegistrationQuestionCustomField,
): boolean => Boolean(getRegistrationQuestionIdForCustomField(customField));

const isRegistrationQuestionFormCustomField = ({
  id,
  presetKey,
}: Pick<EventCustomField, "id" | "presetKey">): boolean =>
  registrationQuestionIds.has(id) ||
  Boolean(presetKey && registrationQuestionPresetKeys.has(presetKey));

export const registrationQuestionsToSettingsJson = ({
  registrationQuestions,
}: RegistrationQuestionsToCustomFieldInputsInput): string => {
  const questions = (
    registrationQuestions || createDefaultRegistrationQuestions()
  ).map(withRegistrationQuestionMetadata);
  const settings: RegistrationQuestionSettings = {
    version: REGISTRATION_QUESTION_SETTINGS_VERSION,
    questions: questions.map((question) => ({
      id: question.id,
      adminTitle: question.adminTitle,
      description: question.description,
      enabled: question.enabled,
      choices: question.choices.map((choice) => ({
        id: choice.id,
        enabled: choice.enabled,
      })),
      label: question.label,
      helperText: question.helperText,
      customFieldId: question.customFieldId || question.id,
      required: question.required ?? false,
      backendFieldType:
        question.backendFieldType ||
        getDefaultRegistrationQuestionFieldType(question),
      scope: question.scope || CmpEventCustomFieldScopeEnum.Attendee,
      appliesToTicketTypeIds: question.appliesToTicketTypeIds || [],
      appliesToSponsorshipPackageIds:
        question.appliesToSponsorshipPackageIds || [],
      sortOrder: question.sortOrder ?? 0,
    })),
  };

  return JSON.stringify(settings);
};

export const registrationQuestionSettingsJsonToQuestions = (
  settingsJson?: string | null,
): EventRegistrationQuestion[] | null => {
  if (!settingsJson) {
    return null;
  }

  let parsedSettings: unknown;
  try {
    parsedSettings = JSON.parse(settingsJson) as unknown;
  } catch {
    return null;
  }

  if (
    !isRecord(parsedSettings) ||
    parsedSettings.version !== REGISTRATION_QUESTION_SETTINGS_VERSION ||
    !Array.isArray(parsedSettings.questions)
  ) {
    return null;
  }

  const settingById = new Map<string, Record<string, unknown>>();
  parsedSettings.questions.forEach((setting) => {
    if (isRecord(setting) && typeof setting.id === "string") {
      settingById.set(setting.id, setting);
    }
  });

  const questions = createDefaultRegistrationQuestions();
  if (questions.some((question) => !settingById.has(question.id))) {
    return null;
  }

  const restoredQuestions: EventRegistrationQuestion[] = [];
  for (let index = 0; index < questions.length; index += 1) {
    const question = questions[index];
    const setting = settingById.get(question.id);
    if (!setting || typeof setting.enabled !== "boolean") {
      return null;
    }

    const choiceSettings = Array.isArray(setting.choices)
      ? setting.choices
      : [];
    const enabledByChoiceId = new Map<string, boolean>();
    for (const choiceSetting of choiceSettings) {
      if (
        !isRecord(choiceSetting) ||
        typeof choiceSetting.id !== "string" ||
        typeof choiceSetting.enabled !== "boolean"
      ) {
        return null;
      }
      enabledByChoiceId.set(choiceSetting.id, choiceSetting.enabled);
    }
    if (question.choices.some((choice) => !enabledByChoiceId.has(choice.id))) {
      return null;
    }

    const defaultQuestion = withRegistrationQuestionMetadata(question, index);
    restoredQuestions.push({
      ...defaultQuestion,
      adminTitle:
        typeof setting.adminTitle === "string"
          ? setting.adminTitle
          : question.adminTitle,
      description:
        typeof setting.description === "string"
          ? setting.description
          : question.description,
      enabled: setting.enabled,
      choices: question.choices.map((choice) => ({
        ...choice,
        enabled: enabledByChoiceId.get(choice.id) ?? choice.enabled,
      })),
      label: typeof setting.label === "string" ? setting.label : question.label,
      helperText:
        typeof setting.helperText === "string"
          ? setting.helperText
          : question.helperText,
      customFieldId:
        typeof setting.customFieldId === "string" && setting.customFieldId
          ? setting.customFieldId
          : question.id,
      required:
        typeof setting.required === "boolean" ? setting.required : false,
      backendFieldType: isCustomFieldType(setting.backendFieldType)
        ? setting.backendFieldType
        : defaultQuestion.backendFieldType,
      scope: isCustomFieldScope(setting.scope)
        ? setting.scope
        : CmpEventCustomFieldScopeEnum.Attendee,
      appliesToTicketTypeIds: isStringArray(setting.appliesToTicketTypeIds)
        ? setting.appliesToTicketTypeIds
        : [],
      appliesToSponsorshipPackageIds: isStringArray(
        setting.appliesToSponsorshipPackageIds,
      )
        ? setting.appliesToSponsorshipPackageIds
        : [],
      sortOrder:
        typeof setting.sortOrder === "number" &&
        Number.isFinite(setting.sortOrder)
          ? setting.sortOrder
          : index,
    });
  }

  return restoredQuestions;
};

// Enabled questions use the event custom-field contract so the public
// registration form can render them. The settings JSON is the admin source of
// truth for disabled parents/choices; legacy records without it are inferred
// from recognized custom fields and keep the visible defaults when none exist.
export const customFieldsToRegistrationQuestions = ({
  customFields,
  registrationQuestionSettingsJson,
}: CustomFieldsToRegistrationQuestionsInput): EventRegistrationQuestion[] => {
  const questions = createDefaultRegistrationQuestions().map(
    withRegistrationQuestionMetadata,
  );
  const customFieldById = new Map<string, RegistrationQuestionCustomField>();
  customFields.forEach((customField) => {
    const stableQuestionId =
      getRegistrationQuestionIdForCustomField(customField);
    if (!stableQuestionId) {
      return;
    }

    const existingField = customFieldById.get(stableQuestionId);
    if (!existingField || customField.id === stableQuestionId) {
      customFieldById.set(stableQuestionId, customField);
    }
  });
  const settingsQuestions = registrationQuestionSettingsJsonToQuestions(
    registrationQuestionSettingsJson,
  );
  if (settingsQuestions) {
    const customFieldBySourceId = new Map(
      customFields.map((customField) => [customField.id, customField]),
    );
    return settingsQuestions.map((question) => {
      const sourceField =
        customFieldBySourceId.get(question.customFieldId || question.id) ||
        customFieldById.get(question.id);
      if (!sourceField) {
        return question;
      }

      return {
        ...question,
        customFieldId: sourceField.id,
        required: sourceField.required,
        backendFieldType: sourceField.fieldType,
        scope: sourceField.scope,
        appliesToTicketTypeIds: sourceField.appliesToTicketTypeIds,
        appliesToSponsorshipPackageIds:
          sourceField.appliesToSponsorshipPackageIds,
        sortOrder: sourceField.sortOrder ?? question.sortOrder,
      };
    });
  }
  const hasPersistedRegistrationQuestions = customFieldById.size > 0;

  if (!hasPersistedRegistrationQuestions) {
    return questions;
  }

  return questions.map((question) => {
    const customField = customFieldById.get(question.id);
    if (!customField) {
      return { ...question, enabled: false };
    }

    const metadata = {
      customFieldId: customField.id,
      required: customField.required,
      backendFieldType: customField.fieldType,
      scope: customField.scope,
      appliesToTicketTypeIds: customField.appliesToTicketTypeIds,
      appliesToSponsorshipPackageIds:
        customField.appliesToSponsorshipPackageIds,
      sortOrder: customField.sortOrder ?? question.sortOrder,
    };

    if (question.kind === "speakerQuestion") {
      return {
        ...question,
        ...metadata,
        enabled: true,
        label:
          customField.label === question.adminTitle ? "" : customField.label,
        helperText: customField.helperText || question.helperText,
      };
    }

    const enabledChoiceLabels = new Set(
      customField.choices.map(normalizeRegistrationQuestionLabel),
    );

    return {
      ...question,
      ...metadata,
      adminTitle: customField.label || question.adminTitle,
      description: customField.helperText || question.description,
      enabled: true,
      choices: question.choices.map((choice) => ({
        ...choice,
        enabled: enabledChoiceLabels.has(
          normalizeRegistrationQuestionLabel(choice.label),
        ),
      })),
    };
  });
};

type RegistrationQuestionsToCustomFieldInputsInput = {
  registrationQuestions?: EventRegistrationQuestion[];
};

export const registrationQuestionsToCustomFieldInputs = ({
  registrationQuestions,
}: RegistrationQuestionsToCustomFieldInputsInput): CmpEventCustomFieldInput[] =>
  (registrationQuestions || createDefaultRegistrationQuestions())
    .map(withRegistrationQuestionMetadata)
    .flatMap((question, index) => {
      if (!question.enabled) {
        return [];
      }

      const isMultiSelect = question.kind === "multiSelect";
      return [
        {
          id: question.customFieldId || question.id,
          label:
            question.kind === "speakerQuestion"
              ? question.label.trim() || question.adminTitle
              : question.adminTitle,
          fieldType:
            question.backendFieldType ||
            getDefaultRegistrationQuestionFieldType(question),
          scope: question.scope || CmpEventCustomFieldScopeEnum.Attendee,
          required: question.required ?? false,
          helperText: isMultiSelect
            ? question.description || undefined
            : question.helperText || question.description || undefined,
          choices: isMultiSelect
            ? question.choices
                .filter((choice) => choice.enabled)
                .map((choice) => choice.label)
            : [],
          appliesToTicketTypeIds: question.appliesToTicketTypeIds || [],
          appliesToSponsorshipPackageIds:
            question.appliesToSponsorshipPackageIds || [],
          sortOrder: question.sortOrder ?? index,
        },
      ];
    });

export const eventReturnToFormState = (
  event: CmpEventFieldsFragment,
  timeZone?: string | null,
): EventFormState => {
  const eventTimeZone = timeZone || event.timeZone;
  const start = utcMilliToDateTimeInputParts(
    event.startAtUtcMilli,
    eventTimeZone,
  );
  const end = utcMilliToDateTimeInputParts(event.endAtUtcMilli, eventTimeZone);
  const inPersonEnabled =
    event.location.mode === CmpEventLocationModeEnum.InPerson ||
    event.location.mode === CmpEventLocationModeEnum.Hybrid;
  const virtualEnabled =
    event.location.mode === CmpEventLocationModeEnum.Virtual ||
    event.location.mode === CmpEventLocationModeEnum.Hybrid;

  return {
    id: event.id,
    slug: event.slug,
    title: event.title,
    eventTypeId: event.eventTypeId,
    eventType: event.eventTypeLabelSnapshot,
    eventTypeColor: event.eventTypeColorSnapshot,
    description: event.description,
    featuredBanner: event.featuredImageUrl || "",
    startDate: start.date,
    startTime: start.time,
    endDate: end.date,
    endTime: end.time,
    inPersonEnabled,
    virtualEnabled,
    venueName: event.location.venueName || "",
    address: event.location.streetAddress || "",
    virtualAccessInstructions: event.location.virtualDetails || "",
    capacity: event.maxAttendeeCapacity
      ? String(event.maxAttendeeCapacity)
      : "",
    publicRegistrationEnabled: event.publicRegistrationEnabled,
    tickets: event.tickets.map((ticket) => ({
      id: ticket.id,
      name: ticket.name,
      description: ticket.description || "",
      // POL-005: editable prices load as the canonical CurrencyInput shape
      // ("85.00", no "$"/commas) so an untouched Update reload stays
      // JSON-equal to the wizard form state (hasUnsavedChanges stability).
      price: centsToCurrencyValue(ticket.priceInCents),
      attendeesPerTicket: String(ticket.attendeesPerTicket || 1),
      memberOnly: ticket.audience === CmpEventTicketAudienceEnum.ActiveMembers,
      code: ticket.code || "",
      salesStartDate: utcMilliToDateInput(
        ticket.salesStartUtcMilli,
        eventTimeZone,
      ),
      salesEndDate: utcMilliToDateInput(ticket.salesEndUtcMilli, eventTimeZone),
      availabilityLimit: ticket.availabilityLimit
        ? String(ticket.availabilityLimit)
        : "",
      visibleOnRegistrationForm: ticket.visibleOnRegistrationForm ?? true,
    })),
    customFields: event.customFields
      .filter((customField) => !isRegistrationQuestionCustomField(customField))
      .map((customField) => ({
        id: customField.id,
        label: customField.label,
        type: getFieldTypeLabel(customField.fieldType),
        required: customField.required,
        choices: formatCustomFieldChoices(customField.choices),
        backendChoices: customField.choices,
        backendFieldType: customField.fieldType,
        scope: customField.scope,
        helperText: customField.helperText || undefined,
        appliesToTicketTypeIds: customField.appliesToTicketTypeIds,
        appliesToSponsorshipPackageIds:
          customField.appliesToSponsorshipPackageIds,
        sortOrder: customField.sortOrder ?? undefined,
      })),
    sponsorshipEnabled: event.sponsorshipEnabled,
    sponsorshipPackages: event.sponsorshipPackages.map(
      (sponsorshipPackage) => ({
        id: sponsorshipPackage.id,
        name: sponsorshipPackage.name,
        description: sponsorshipPackage.description || "",
        // POL-005: canonical decimal-dollars shape (see ticket.price above).
        price: centsToCurrencyValue(sponsorshipPackage.priceInCents),
        maxAvailable: sponsorshipPackage.maxAvailable
          ? String(sponsorshipPackage.maxAvailable)
          : "",
        includedTickets: String(
          sponsorshipPackage.includedAttendeeQuantity || 0,
        ),
        includedTicketTypeId:
          sponsorshipPackage.includedAttendeeTicketTypeId || "",
        publicDisplayEnabled: sponsorshipPackage.publicDisplayEnabled,
        code: sponsorshipPackage.code || "",
        heroBannerEnabled: !!sponsorshipPackage.heroBannerEnabled,
        additionalBenefits: sponsorshipPackage.additionalBenefits || "",
        addOns: (sponsorshipPackage.addOns || []).map((addOn) => ({
          id: addOn.id,
          description: addOn.description,
          code: addOn.code || "",
          quantity: String(addOn.quantity),
        })),
      }),
    ),
    costs: event.internalCosts.map((cost) => ({
      id: cost.id,
      label: cost.label,
      amount: formatCents(cost.amountInCents),
    })),
    responsibilities: event.responsibilities.map((responsibility) => ({
      id: responsibility.id,
      owner: responsibility.assignedTo || "",
      task: responsibility.label,
    })),
    internalNotes: event.notes[0]?.body || "",
    status: getStatusLabel(event.status),
    visibility: getVisibilityLabel(event.visibility),
    attendeeCount: event.attendeeCount,
    sponsorRequestCount: 0,
    ticketRevenue: event.ticketRevenueInCents / 100,
    sponsorshipRevenue: event.sponsorshipRevenueInCents / 100,
    eventCode: event.eventCode || "",
    heroShortDescription: event.heroShortDescription || "",
    registrationDetailsHtml: event.registrationDetailsHtml || "",
    specialVenueConsiderations: event.specialVenueConsiderations || "",
    dressCode: getDressCodeLabel(event.dressCode),
    speakers: (event.speakers || []).map((speaker) => ({
      id: speaker.id,
      name: speaker.name,
      company: speaker.company || "",
      title: speaker.title || "",
      imageUrl: speaker.imageUrl || "",
      bio: speaker.bio || "",
    })),
    agendaItems: (event.agendaItems || []).map((agendaItem) => ({
      id: agendaItem.id,
      timeLabel: agendaItem.timeLabel,
      details: agendaItem.details,
    })),
    eventIsFree: !!event.eventIsFree,
    displayRemainingTicketQuantity:
      event.displayRemainingTicketQuantity ?? true,
    displayRemainingTicketTypeQuantity:
      !!event.displayRemainingTicketTypeQuantity,
    allowWaitlist: !!event.allowWaitlist,
    city: event.location.city || "",
    province: event.location.province || "",
    postalCode: event.location.postalCode || "",
    country: event.location.country || "",
    registrationQuestions: customFieldsToRegistrationQuestions({
      customFields: event.customFields,
      registrationQuestionSettingsJson: event.registrationQuestionSettingsJson,
    }),
  };
};

const getStatusEnum = (status: EventStatus): CmpEventStatusEnum => {
  switch (status) {
    case "Published":
      return CmpEventStatusEnum.Published;
    case "Live":
      return CmpEventStatusEnum.Live;
    case "Completed":
      return CmpEventStatusEnum.Completed;
    case "Cancelled":
      return CmpEventStatusEnum.Canceled;
    case "Draft":
    default:
      return CmpEventStatusEnum.Draft;
  }
};

const getVisibilityEnum = (
  visibility: EventVisibility,
): CmpEventVisibilityEnum =>
  visibility === "Private"
    ? CmpEventVisibilityEnum.Private
    : CmpEventVisibilityEnum.Public;

const getFieldTypeEnum = (
  fieldType: EventCustomField["type"],
): CmpEventCustomFieldTypeEnum => {
  switch (fieldType) {
    case "Select":
      return CmpEventCustomFieldTypeEnum.Select;
    case "Multi-select":
      return CmpEventCustomFieldTypeEnum.MultiSelect;
    case "Checkbox":
      return CmpEventCustomFieldTypeEnum.Checkbox;
    case "File upload":
      return CmpEventCustomFieldTypeEnum.FileUpload;
    case "Text":
    default:
      return CmpEventCustomFieldTypeEnum.Text;
  }
};

const buildFreeEventTicketInput = (event: EventFormState) => {
  // Only reuse a registration-visible public free ticket (a previously saved
  // free event's GA ticket). Hidden seed rows like NCST/Complimentary must
  // never be picked — they carry sponsorship copy and availability caps that
  // would silently limit a free event's registrations. When nothing matches,
  // fall through to the free-general-admission defaults below.
  const existingPublicFreeTicket = event.tickets.find(
    (ticket) =>
      !ticket.memberOnly &&
      parseCents(ticket.price) === 0 &&
      ticket.visibleOnRegistrationForm !== false,
  );

  return {
    id: existingPublicFreeTicket?.id || "free-general-admission",
    code: existingPublicFreeTicket?.code || "FREE",
    name: "General Admission",
    description:
      existingPublicFreeTicket?.description || "Free event registration.",
    priceInCents: 0,
    attendeesPerTicket: 1,
    audience: CmpEventTicketAudienceEnum.Everyone,
    attendanceMode: CmpEventAttendanceModeEnum.Either,
    salesStartUtcMilli: undefined,
    salesEndUtcMilli: undefined,
    availabilityLimit: existingPublicFreeTicket?.availabilityLimit
      ? parseOptionalNumber(existingPublicFreeTicket.availabilityLimit)
      : null,
    visibleOnRegistrationForm: true,
    sortOrder: 0,
  };
};

export const eventFormStateToInput = ({
  event,
  timeZone,
}: {
  event: EventFormState;
  timeZone: string;
}): CmpEventDataInput => {
  const normalizedTimeZone = normalizeCmpTimeZone(timeZone).timeZone;
  const locationMode =
    event.inPersonEnabled && event.virtualEnabled
      ? CmpEventLocationModeEnum.Hybrid
      : event.virtualEnabled
        ? CmpEventLocationModeEnum.Virtual
        : CmpEventLocationModeEnum.InPerson;
  const tickets = event.eventIsFree
    ? [buildFreeEventTicketInput(event)]
    : event.tickets.map((ticket, index) => ({
        id: ticket.id,
        code: ticket.code || undefined,
        name: ticket.name,
        description: ticket.description || undefined,
        priceInCents: parseCents(ticket.price),
        attendeesPerTicket:
          parseOptionalNumber(ticket.attendeesPerTicket || "1") || 1,
        audience: ticket.memberOnly
          ? CmpEventTicketAudienceEnum.ActiveMembers
          : CmpEventTicketAudienceEnum.Everyone,
        attendanceMode: CmpEventAttendanceModeEnum.Either,
        salesStartUtcMilli: dateInputToUtcMilli({
          boundary: "start",
          timeZone: normalizedTimeZone,
          value: ticket.salesStartDate,
        }),
        salesEndUtcMilli: dateInputToUtcMilli({
          boundary: "end",
          timeZone: normalizedTimeZone,
          value: ticket.salesEndDate,
        }),
        availabilityLimit: ticket.availabilityLimit
          ? parseOptionalNumber(ticket.availabilityLimit)
          : null,
        visibleOnRegistrationForm: ticket.visibleOnRegistrationForm ?? true,
        sortOrder: index,
      }));
  const registrationQuestionCustomFields =
    registrationQuestionsToCustomFieldInputs({
      registrationQuestions: event.registrationQuestions,
    });
  const customFields: CmpEventCustomFieldInput[] = [
    ...registrationQuestionCustomFields,
    ...event.customFields
      .filter(
        (customField) => !isRegistrationQuestionFormCustomField(customField),
      )
      .map((customField, index) => ({
        id: customField.id,
        label: customField.label,
        fieldType:
          customField.backendFieldType || getFieldTypeEnum(customField.type),
        scope: customField.scope || CmpEventCustomFieldScopeEnum.Attendee,
        required: customField.required,
        helperText: customField.helperText || undefined,
        choices: getCustomFieldChoicesForSave(customField),
        appliesToTicketTypeIds: customField.appliesToTicketTypeIds || [],
        appliesToSponsorshipPackageIds:
          customField.appliesToSponsorshipPackageIds || [],
        sortOrder:
          customField.sortOrder ??
          registrationQuestionCustomFields.length + index,
      })),
  ];

  return {
    id: event.id === "new-event" ? undefined : event.id,
    slug: event.slug || undefined,
    title: event.title,
    description: event.description,
    status: getStatusEnum(event.status),
    startAtUtcMilli: dateTimeInputPartsToUtcMilli(
      event.startDate,
      event.startTime,
      normalizedTimeZone,
    ),
    endAtUtcMilli: dateTimeInputPartsToUtcMilli(
      event.endDate,
      event.endTime,
      normalizedTimeZone,
    ),
    timeZone: normalizedTimeZone,
    eventTypeId: event.eventTypeId,
    eventTypeLabelSnapshot: event.eventType,
    eventTypeColorSnapshot: event.eventTypeColor,
    featuredImageUrl: event.featuredBanner || undefined,
    visibility: getVisibilityEnum(event.visibility),
    publicRegistrationEnabled: event.publicRegistrationEnabled,
    eventCode: event.eventCode || undefined,
    heroShortDescription: event.heroShortDescription || undefined,
    registrationDetailsHtml: event.registrationDetailsHtml || undefined,
    specialVenueConsiderations: event.specialVenueConsiderations || undefined,
    dressCode: getDressCodeEnum(event.dressCode),
    speakers: (event.speakers || []).map((speaker, index) => ({
      id: speaker.id,
      name: speaker.name,
      company: speaker.company || undefined,
      title: speaker.title || undefined,
      imageUrl: speaker.imageUrl || undefined,
      bio: speaker.bio || undefined,
      sortOrder: index,
    })),
    agendaItems: (event.agendaItems || []).map((agendaItem, index) => ({
      id: agendaItem.id,
      timeLabel: agendaItem.timeLabel,
      details: agendaItem.details,
      sortOrder: index,
    })),
    eventIsFree: event.eventIsFree ?? false,
    displayRemainingTicketQuantity:
      event.displayRemainingTicketQuantity ?? true,
    displayRemainingTicketTypeQuantity:
      event.displayRemainingTicketTypeQuantity ?? false,
    allowWaitlist: event.allowWaitlist ?? false,
    maxAttendeeCapacity: parseOptionalNumber(event.capacity),
    location: {
      mode: locationMode,
      venueName: event.inPersonEnabled ? event.venueName : undefined,
      streetAddress: event.inPersonEnabled ? event.address : undefined,
      city: event.inPersonEnabled ? event.city || undefined : undefined,
      province: event.inPersonEnabled ? event.province || undefined : undefined,
      postalCode: event.inPersonEnabled
        ? event.postalCode || undefined
        : undefined,
      country: event.inPersonEnabled ? event.country || undefined : undefined,
      virtualDetails: event.virtualEnabled
        ? event.virtualAccessInstructions
        : undefined,
    },
    tickets,
    customFields,
    registrationQuestionSettingsJson: registrationQuestionsToSettingsJson({
      registrationQuestions: event.registrationQuestions,
    }),
    sponsorshipEnabled: event.sponsorshipEnabled,
    sponsorshipPackages: event.sponsorshipPackages.map(
      (sponsorshipPackage, index) => ({
        id: sponsorshipPackage.id,
        code: sponsorshipPackage.code || undefined,
        name: sponsorshipPackage.name,
        description: sponsorshipPackage.description || undefined,
        priceInCents: parseCents(sponsorshipPackage.price),
        maxAvailable: parseOptionalNumber(sponsorshipPackage.maxAvailable),
        publicDisplayEnabled: sponsorshipPackage.publicDisplayEnabled,
        heroBannerEnabled: sponsorshipPackage.heroBannerEnabled ?? false,
        includedAttendeeQuantity:
          parseOptionalNumber(sponsorshipPackage.includedTickets) || 0,
        includedAttendeeTicketTypeId:
          sponsorshipPackage.includedTicketTypeId || undefined,
        additionalBenefits: sponsorshipPackage.additionalBenefits || undefined,
        addOns: (sponsorshipPackage.addOns || []).map((addOn) => ({
          id: addOn.id,
          description: addOn.description,
          code: addOn.code || undefined,
          quantity: parseOptionalNumber(addOn.quantity) || 0,
        })),
        sortOrder: index,
      }),
    ),
    internalCosts: event.costs.map((cost) => ({
      id: cost.id,
      label: cost.label,
      amountInCents: parseCents(cost.amount),
    })),
    responsibilities: event.responsibilities.map((responsibility) => ({
      id: responsibility.id,
      label: responsibility.task,
      assignedTo: responsibility.owner || undefined,
    })),
    notes: event.internalNotes
      ? [
          {
            id: "internal-note",
            body: event.internalNotes,
            visibility: "STAFF",
          },
        ]
      : [],
  };
};
